import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation, Navigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { errorMessage } from "@/lib/api";
import { DEMO_ACCOUNTS, demoCredentials, safeNext } from "@/lib/authSync";
import { BrandMark } from "@/components/common/BrandLogo";
import { ModulesAnimation, useBrandAnimation } from "@/components/common/BrandAnimation";
import { Loader2, Eye, EyeOff, ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";

const DEMO = DEMO_ACCOUNTS;

const REASONS = [
  ["01", "Totalentreprise", "fra grunn til ferdig bygg"],
  ["02", "Fleksibilitet", "innebygd i alle løsninger"],
  ["03", "Raskt og trygt", "levert til avtalt pris"],
];

export default function LoginPage() {
  const { login, user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [demo, setDemo] = useState(null);
  const anim = useBrandAnimation();

  /* Demo-inngang (`/login?demo=leder&next=/planlegging`): prototypen vist i rammer (t.d. tilbodet) loggar inn i ein
     demo-konto utan å vise skjemaet. Berre demo-kontoane; `next` må vere ein intern sti. */
  const demoCreds = useMemo(() => demoCredentials(params.get("demo")), [params]);
  const next = safeNext(params.get("next"));
  const [autoState, setAutoState] = useState(demoCreds ? "pending" : "off");
  const autoRan = useRef(false);
  useEffect(() => {
    if (!demoCreds || loading || user || autoRan.current) return;
    autoRan.current = true;
    login(demoCreds.email, demoCreds.password)
      .then((u) => navigate(next || (u.role === "felt" ? "/felt" : "/"), { replace: true }))
      .catch((err) => {
        setAutoState("failed");
        setEmail(demoCreds.email);
        setPassword(demoCreds.password);
        setError(errorMessage(err, "Innlogging feilet"));
      });
  }, [demoCreds, loading, user, login, navigate, next]);

  if (!loading && user) return <Navigate to={next || (user.role === "felt" ? "/felt" : "/")} replace />;

  if (autoState === "pending") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background text-muted-foreground" data-testid="login-demo-auto" aria-busy="true">
        <div className="inline-flex items-center gap-2.5 text-[13px]"><Loader2 className="h-4 w-4 animate-spin" /> Opnar prototypen …</div>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const u = await login(email.trim(), password);
      navigate(u.role === "felt" ? "/felt" : location.state?.from || "/", { replace: true });
    } catch (err) {
      setError(errorMessage(err, "Innlogging feilet"));
    } finally {
      setBusy(false);
    }
  };

  const fillDemo = (key) => {
    const c = demoCredentials(key);
    if (!c) return;
    setDemo(key);
    setEmail(c.email);
    setPassword(c.password);
    setError("");
  };

  return (
    <div className="min-h-screen bg-background lg:grid lg:grid-cols-[minmax(0,1.15fr)_minmax(440px,0.85fr)]" data-testid="login-page">
      {/* brand panel */}
      <aside className="login-hero relative hidden overflow-hidden bg-navy text-white lg:flex lg:flex-col lg:justify-between lg:p-10 xl:p-14" aria-label="Stord Innkvartering">
        <div className="module-grid-bg pointer-events-none absolute inset-0 opacity-70" aria-hidden />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-[45%] bg-gradient-to-t from-navy via-navy/70 to-transparent" aria-hidden />

        <div className="relative flex items-center gap-2.5" data-testid="brand-logo">
          <BrandMark className="h-7 w-7" color="#fff" />
          <div className="leading-tight">
            <div className="text-[14px] font-semibold tracking-tight">Stord Innkvartering</div>
            <div className="text-[11.5px] text-white/60">Driftsplattform</div>
          </div>
        </div>

        <div className="relative mx-auto w-full max-w-[680px] py-6">
          <ModulesAnimation data={anim} className="w-full drop-shadow-[0_30px_60px_rgba(0,0,0,0.35)]" />
        </div>

        <div className="relative">
          <h2 className="font-display max-w-[560px] text-[34px] font-semibold leading-[1.1] tracking-tight xl:text-[40px]">Modulbygg like fleksible som ditt prosjekt.</h2>
          <p className="mt-3 max-w-[520px] text-[14.5px] leading-6 text-white/75">Fra henvendelse til drift: kapasitet, rigg, transport, innkvartering og økonomi – i ett system.</p>
          <dl className="mt-8 grid max-w-[640px] grid-cols-3 gap-6 border-t border-white/15 pt-6">
            {REASONS.map(([n, t, s]) => (
              <div key={n}>
                <dt className="font-mono text-[11px] text-white/50">{n}</dt>
                <dd className="mt-1 text-[13.5px] font-semibold leading-5">{t}</dd>
                <dd className="text-[12.5px] leading-5 text-white/65">{s}</dd>
              </div>
            ))}
          </dl>
        </div>
      </aside>

      {/* form panel */}
      <main className="flex min-h-screen flex-col">
        {/* compact brand band on small screens */}
        <div className="relative overflow-hidden bg-navy px-5 pb-4 pt-5 text-white lg:hidden" data-testid="login-mobile-brand">
          <div className="module-grid-bg pointer-events-none absolute inset-0 opacity-60" aria-hidden />
          <div className="relative flex items-center gap-2.5">
            <BrandMark className="h-6 w-6" color="#fff" />
            <div className="text-[13.5px] font-semibold tracking-tight">Stord Innkvartering <span className="font-normal text-white/60">· Driftsplattform</span></div>
          </div>
          <div className="relative mx-auto -mb-2 mt-1 w-full max-w-[380px]"><ModulesAnimation data={anim} className="w-full" /></div>
        </div>

        <header className="hidden h-14 items-center justify-end px-8 lg:flex">
          <a href="/henvendelse" className="link inline-flex items-center gap-1 text-[12.5px]" data-testid="login-public-link">Send en henvendelse <ArrowRight className="h-3.5 w-3.5" /></a>
        </header>

        <div className="flex flex-1 items-center justify-center px-5 py-10 sm:px-8">
          <div className="w-full max-w-[380px] fade-up">
            <h1 className="font-display text-[26px] font-semibold leading-8 tracking-tight">Logg inn</h1>
            <p className="mt-1.5 text-[13.5px] text-muted-foreground">Bruk bedriftskontoen din for å fortsette til driftsplattformen.</p>

            <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form" noValidate>
              <div className="space-y-1.5">
                <Label htmlFor="email" className="text-[12.5px]">E-post</Label>
                <Input id="email" type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required className="h-10" placeholder="navn@bedrift.no" data-testid="login-email-input" autoFocus />
              </div>
              <div className="space-y-1.5">
                <div className="flex items-baseline justify-between">
                  <Label htmlFor="password" className="text-[12.5px]">Passord</Label>
                </div>
                <div className="relative">
                  <Input id="password" type={showPw ? "text" : "password"} autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required className="h-10 pr-10" data-testid="login-password-input" />
                  <button type="button" onClick={() => setShowPw((v) => !v)} className="absolute inset-y-0 right-0 inline-flex w-10 items-center justify-center text-muted-foreground transition-colors duration-150 hover:text-foreground focus-visible:outline-none focus-visible:text-foreground" aria-label={showPw ? "Skjul passord" : "Vis passord"} aria-pressed={showPw} data-testid="login-toggle-password">
                    {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              {error && (
                <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-[12.5px] text-rose-900 dark:border-rose-900/60 dark:bg-rose-950/40 dark:text-rose-200" role="alert" data-testid="login-error">{error}</div>
              )}
              <Button type="submit" size="lg" className="h-10 w-full" disabled={busy || !email || !password} data-testid="login-submit-button">
                {busy && <Loader2 className="animate-spin" />} Logg inn
              </Button>
            </form>

            <div className="mt-10 border-t pt-5" data-testid="login-demo-accounts">
              <div className="flex items-baseline justify-between">
                <div className="text-[12.5px] font-medium">Demokontoer</div>
                <div className="text-[11.5px] text-muted-foreground">Velg rolle for å fylle inn</div>
              </div>
              <div className="mt-2.5 grid grid-cols-3 gap-1.5">
                {DEMO.map(([k, l]) => (
                  <button key={k} type="button" onClick={() => fillDemo(k)} aria-pressed={demo === k}
                    className={cn("h-8 rounded-md border px-2 text-[12px] transition-colors duration-150 hover:bg-muted focus-visible:bg-muted focus-visible:outline-none", demo === k ? "border-foreground/40 bg-muted font-medium" : "border-border text-foreground/80")}
                    data-testid={`login-demo-${k}`}>
                    {l}
                  </button>
                ))}
              </div>
            </div>
            <a href="/henvendelse" className="link mt-8 inline-flex items-center gap-1 text-[12.5px] lg:hidden" data-testid="login-public-link-mobile">Send en henvendelse <ArrowRight className="h-3.5 w-3.5" /></a>
          </div>
        </div>

        <footer className="flex h-11 items-center justify-between px-5 text-[11.5px] text-muted-foreground sm:px-8">
          <span>Stord Innkvartering AS · Heiane 8, 5412 Stord</span>
          <a href="https://stordinnkvartering.no" target="_blank" rel="noreferrer" className="hidden hover:text-foreground sm:inline">stordinnkvartering.no</a>
        </footer>
      </main>
    </div>
  );
}
